/**
 * Entry panel — forecast entry form, master/child creation, CSV upload,
 * bulk creation, and the "tinder" review flow (accept/decline assets).
 * Extracted from app.js without logic changes.
 */

import { els } from "../dom";
import { getState, updateState, subscribe } from "../state";
import { formatNumber } from "../utils/formatting";
import { escapeHtml } from "../utils/escapeHtml";
import { onPanelActivate, getActivePanel } from "../router";
import { getProvider } from "../providerInstance";
import { debounce } from "../components/filters";
import { showLoading, hideLoading } from "../components/LoadingOverlay";
import {
  buildMasterCsvTemplate,
  validateMasterCsvRows,
  buildErrorReportCsv,
  buildEanVariationPreview,
  buildLocalCsvTemplate,
  validateLocalCsvRows,
  parseCsv,
  MASTER_CSV_TEMPLATE_COLUMNS,
  LOCAL_CSV_TEMPLATE_COLUMNS,
  type MasterCsvRowValidation,
} from "../components/csv";
import { showToast } from "../components/Toast";
import {
  normalizeValue,
  unique,
  fillSelect,
  inferQuarterFromDate,
  MAX_RENDERED_ROWS,
  canCurrentRoleWrite,
  setFormMessage,
  isActionableMaster,
  buildReviewedMasterIdSet,
  enrichAssetsWithVolume,
  createChildFromDecision,
  processCreatorRows,
  parseCsvToRows,
} from "./shared";
import { applyDashboardFiltersAndRender } from "./dashboard";

// audit M1: cross-panel render callbacks were removed — every panel now
// subscribes to the state keys it cares about (assets, forecasts, rules,
// reviewedMasterIds) and re-renders via onPanelActivate when the user
// comes back to it. See commit "audit M1" for the rationale.

// ── Wizard reset callback (set by bindWizardNav, used by bindCreatorFlow) ──
let _resetWizard: (() => void) | null = null;

// ── Init ─────────────────────────────────────────────────────

export function init(): void {
  onPanelActivate("entry", render);
  subscribe("assets", render);
  subscribe("forecasts", render);
  subscribe("rules", render);
  subscribe("reviewedMasterIds", render);
  bindEntryFlow();
  bindCreatorFlow();
  bindCreatorFormInteractions();
  bindLocalProjectFlow();
  bindViewToggle();
  bindCreatorTabs();
  bindWizardNav();
  bindCsvUpload();
  bindBulkEntry();
  // N6: live EAN ↔ variation positional preview
  bindEanVariationPreview();
}

// ── Render ───────────────────────────────────────────────────

export function render(): void {
  if (getActivePanel() !== "entry") return;
  renderEntryMarketFilterOptions();
  renderEntryMarketView();
  renderCreatorFormOptions();
  renderLocalProjectOptions();
}

// ── Message helpers ──────────────────────────────────────────

function setCreatorMessage(message: string, isError = false): void {
  if (!els.creatorMessage) return;
  els.creatorMessage.textContent = message;
  els.creatorMessage.classList.toggle("error", isError);
  els.creatorMessage.classList.toggle("ok", !isError && Boolean(message));
}

function setLocalMessage(message: string, isError = false): void {
  if (!els.localMessage) return;
  els.localMessage.textContent = message;
  els.localMessage.classList.toggle("error", isError);
  els.localMessage.classList.toggle("ok", !isError && Boolean(message));
}

function setMarketMessage(message: string, isError = false): void {
  if (!els.marketMessage) return;
  els.marketMessage.textContent = message;
  els.marketMessage.classList.toggle("error", isError);
  els.marketMessage.classList.toggle("ok", !isError && Boolean(message));
}

// ── Helper to trigger a data refresh ─────────────────────────

import { triggerRefresh } from "../refreshService";

// ── Entry flow (tinder-like review) ──────────────────────────

function bindEntryFlow(): void {
  const debouncedMarketRender = debounce(renderEntryMarketView, 120);
  els.marketFilter?.addEventListener("input", debouncedMarketRender);
  els.marketStatusFilter?.addEventListener("change", renderEntryMarketView);
  els.marketHubFilter?.addEventListener("change", renderEntryMarketView);
  els.marketBrandEntryFilter?.addEventListener("change", renderEntryMarketView);
  els.marketDivisionFilter?.addEventListener("change", renderEntryMarketView);
  els.marketValidateSelectedBtn?.addEventListener("click", validateSelectedMarketRows);

  els.marketSelectAll?.addEventListener("change", (event) => {
    const checked = Boolean((event.target as HTMLInputElement).checked);
    for (const checkbox of document.querySelectorAll('input[data-market-select="1"]')) {
      (checkbox as HTMLInputElement).checked = checked;
    }
  });

  els.marketTableBody?.addEventListener("click", (event: Event) => {
    if (!canCurrentRoleWrite()) {
      setFormMessage("Read-only role: you cannot validate forecasts.", true);
      return;
    }
    const btn = (event.target as HTMLElement).closest(
      "button[data-action='review-yes'],button[data-action='review-no']",
    ) as HTMLElement | null;
    if (!btn) return;
    const assetId = btn.dataset.id;
    const state = getState();
    const asset = state.assets.find((a) => a.id === assetId);
    if (!asset) return;
    updateState({
      pendingDecision: btn.dataset.action === "review-no" ? "no" : "yes",
    });
    openReviewModal(asset);
  });

  els.modalCancelBtn?.addEventListener("click", () => {
    els.dateModal?.classList.add("hidden");
    updateState({ pendingAccept: null, pendingDecision: "yes" });
  });

  els.modalConfirmBtn?.addEventListener("click", async () => {
    if (!canCurrentRoleWrite()) {
      setFormMessage("Read-only role: you cannot validate forecasts.", true);
      return;
    }
    const state = getState();
    if (state.reviewInFlight) return;

    const launchDate = (els.launchDateInput as HTMLInputElement)?.value;
    const country = (els.launchCountrySelect as HTMLSelectElement)?.value;
    if (!launchDate || !country || !state.pendingAccept) {
      setFormMessage("Launch date and country are required.", true);
      return;
    }

    const asset = state.pendingAccept;
    const decision = state.pendingDecision;
    setFormMessage("Saving decision...");
    els.dateModal?.classList.add("hidden");
    updateState({ reviewInFlight: true });
    if (els.modalConfirmBtn) (els.modalConfirmBtn as HTMLButtonElement).disabled = true;

    try {
      const isAlreadyChild = normalizeValue(asset.parentChild) === "child";
      let child = asset;
      if (!isAlreadyChild) {
        child = await createChildFromDecision(asset, country, launchDate, decision);
        const currentAssets = [...getState().assets];
        currentAssets.push(enrichAssetsWithVolume([child], getState().rules as any)[0]);
        updateState({ assets: currentAssets as any });
      }

      if (decision === "yes") {
        const dedupeKey = `${child.id}::${launchDate}`;
        const currentForecasts = getState().forecasts;
        const alreadyExists = currentForecasts.some(
          (f: any) => `${f.assetId}::${f.date}` === dedupeKey,
        );
        if (!alreadyExists) {
          const provider = getProvider();
          const forecastPayload = {
            assetId: child.id,
            date: launchDate,
            quantity: Number(child.assetVolume || asset.assetVolume || 0),
            owner: country,
            division: asset.division || "",
            brand: asset.brand || "",
            country,
            notes: `Accepted via ${asset.productName || asset.name}`,
          };
          await provider.upsertForecast(forecastPayload);
          const updatedForecasts = [...currentForecasts];
          updatedForecasts.push({
            id: `${child.id}:${launchDate}:${country}`,
            ...forecastPayload,
            createdAt: new Date().toISOString(),
          } as any);
          updateState({ forecasts: updatedForecasts as any });
        }
      }

      updateState({
        pendingAccept: null,
        pendingDecision: "yes",
        reviewedMasterIds: buildReviewedMasterIdSet(
          getState().assets as any,
          getState().forecasts as any,
        ),
      });
      renderEntryMarketView();
      applyDashboardFiltersAndRender();
      // M1: other panels re-render via their own state subscriptions;
      // the previous manual cross-panel render calls were redundant.
      setFormMessage(
        `Decision ${decision.toUpperCase()} saved for ${asset.productName || asset.name}.`,
      );
    } catch (err: any) {
      setFormMessage(`Error: ${err.message}`, true);
    } finally {
      updateState({ reviewInFlight: false });
      if (els.modalConfirmBtn)
        (els.modalConfirmBtn as HTMLButtonElement).disabled = false;
    }
  });
}

// ── Entry market filter options ──────────────────────────────

export function renderEntryMarketFilterOptions(): void {
  const state = getState();
  const selectedHub = (els.marketHubFilter as HTMLSelectElement)?.value || "";
  const selectedBrand = (els.marketBrandEntryFilter as HTMLSelectElement)?.value || "";
  const selectedDivision = (els.marketDivisionFilter as HTMLSelectElement)?.value || "";

  const masters = state.assets.filter((asset) => isActionableMaster(asset));
  fillSelect(els.marketHubFilter, unique(masters.map((asset) => asset.hub)), "All");
  fillSelect(
    els.marketBrandEntryFilter,
    unique(masters.map((asset) => asset.brand)),
    "All",
  );
  fillSelect(
    els.marketDivisionFilter,
    unique(masters.map((asset) => asset.division)),
    "All",
  );

  if (els.marketHubFilter) (els.marketHubFilter as HTMLSelectElement).value = selectedHub;
  if (els.marketBrandEntryFilter)
    (els.marketBrandEntryFilter as HTMLSelectElement).value = selectedBrand;
  if (els.marketDivisionFilter)
    (els.marketDivisionFilter as HTMLSelectElement).value = selectedDivision;
}

// ── Entry market view ────────────────────────────────────────

export function renderEntryMarketView(): void {
  if (!els.marketTableBody) return;

  const state = getState();
  const needle = normalizeValue((els.marketFilter as HTMLSelectElement)?.value || "");
  const statusFilter = normalizeValue(
    (els.marketStatusFilter as HTMLSelectElement)?.value || "",
  );
  const hubFilter = normalizeValue(
    (els.marketHubFilter as HTMLSelectElement)?.value || "",
  );
  const brandFilter = normalizeValue(
    (els.marketBrandEntryFilter as HTMLSelectElement)?.value || "",
  );
  const divisionFilter = normalizeValue(
    (els.marketDivisionFilter as HTMLSelectElement)?.value || "",
  );
  const reviewedSet = state.reviewedMasterIds;

  const filteredAssets = state.assets
    .filter((asset) => isActionableMaster(asset))
    .filter((asset) => {
      const isReviewed = reviewedSet.has(asset.id);
      const status = isReviewed ? "forecasted" : "pending";
      if (statusFilter && status !== statusFilter) return false;
      if (hubFilter && normalizeValue(asset.hub) !== hubFilter) return false;
      if (brandFilter && normalizeValue(asset.brand) !== brandFilter) return false;
      if (divisionFilter && normalizeValue(asset.division) !== divisionFilter)
        return false;

      if (!needle) return true;
      const searchable = [
        asset.id,
        asset.productName,
        asset.name,
        asset.brand,
        asset.division,
        asset.hub,
      ]
        .map((v) => normalizeValue(v))
        .join(" ");
      return searchable.includes(needle);
    });

  const rows = filteredAssets.slice(0, MAX_RENDERED_ROWS).map((asset) => {
    const isReviewed = reviewedSet.has(asset.id);
    const status = isReviewed ? "Forecasted" : "Pending";
    const statusClass = isReviewed ? "forecasted" : "pending";
    return `<tr>
      <td><input type="checkbox" data-market-select="1" data-id="${escapeHtml(asset.id)}" /></td>
      <td>${escapeHtml(asset.productName || asset.name || asset.id)}</td>
      <td>${escapeHtml(asset.division || "")}</td>
      <td>${escapeHtml(asset.brand || "")}</td>
      <td>${escapeHtml(asset.hub || "")}</td>
      <td>${escapeHtml(asset.type || "")}</td>
      <td>${escapeHtml(asset.quarter || "")}</td>
      <td>${escapeHtml(asset.requestedDeliveryDate || "")}</td>
      <td><span class="status-badge status-${statusClass}">${escapeHtml(status)}</span></td>
      <td>
        <button class="btn btn-sm" data-action="review-yes" data-id="${escapeHtml(asset.id)}" type="button">Yes</button>
        <button class="btn btn-sm btn-ghost" data-action="review-no" data-id="${escapeHtml(asset.id)}" type="button">No</button>
      </td>
    </tr>`;
  });

  const truncated =
    filteredAssets.length > MAX_RENDERED_ROWS
      ? `<tr><td colspan="10" style="text-align:center;color:var(--muted)">Showing first ${MAX_RENDERED_ROWS} of ${filteredAssets.length} rows. Use filters to narrow results.</td></tr>`
      : "";

  els.marketTableBody.innerHTML =
    rows.join("") + truncated ||
    '<tr><td colspan="10" style="text-align:center;color:var(--muted)">No products found.</td></tr>';
}

// ── Review modal ─────────────────────────────────────────────

function openReviewModal(asset: any): void {
  updateState({ pendingAccept: asset });
  if (els.modalProductName)
    els.modalProductName.textContent = asset.productName || asset.name;
  if (els.modalAssetVolume)
    els.modalAssetVolume.textContent = formatNumber(asset.assetVolume || 0);

  if (els.launchDateInput) {
    const dateRaw = String(asset.requestedDeliveryDate || "").split(" ")[0];
    (els.launchDateInput as HTMLInputElement).value = dateRaw || "";
  }

  const state = getState();
  const countryOptions = unique([
    asset.hub,
    ...state.assets.filter((a) => a.brand === asset.brand).map((a) => a.hub),
  ]);
  if (els.launchCountrySelect) {
    fillSelect(els.launchCountrySelect, countryOptions, "Select country");
    if (asset.hub) {
      (els.launchCountrySelect as HTMLSelectElement).value = asset.hub;
    }
  }

  els.dateModal?.classList.remove("hidden");
}

// ── Bulk validation ──────────────────────────────────────────

async function validateSelectedMarketRows(): Promise<void> {
  if (!canCurrentRoleWrite()) {
    setMarketMessage("Read-only role: you cannot validate forecasts.", true);
    return;
  }
  const state = getState();
  if (state.reviewInFlight) return;

  const launchDate = (els.marketBulkDate as HTMLInputElement)?.value;
  if (!launchDate) {
    setMarketMessage("Please select a validation date before bulk validation.", true);
    return;
  }

  const selectedIds = [
    ...document.querySelectorAll('input[data-market-select="1"]:checked'),
  ]
    .map((input) => (input as HTMLElement).dataset.id)
    .filter(Boolean) as string[];

  if (!selectedIds.length) {
    setMarketMessage("Please select at least one row.", true);
    return;
  }

  const selectedAssets = selectedIds
    .map((id) => state.assets.find((asset) => asset.id === id))
    .filter(Boolean);

  if (!selectedAssets.length) {
    setMarketMessage("No valid selected assets were found.", true);
    return;
  }

  updateState({ reviewInFlight: true });
  if (els.marketValidateSelectedBtn)
    (els.marketValidateSelectedBtn as HTMLButtonElement).disabled = true;

  const existingForecastKeys = new Set(
    state.forecasts.map((f: any) => `${f.assetId}::${f.date}`),
  );

  let processed = 0;
  let skipped = 0;
  const currentAssets = [...state.assets];
  const currentForecasts = [...state.forecasts];
  const provider = getProvider();

  try {
    for (const asset of selectedAssets) {
      const country = asset!.hub || "";
      if (!country) {
        skipped += 1;
        continue;
      }

      const isAlreadyChild = normalizeValue(asset!.parentChild) === "child";
      let child = asset!;
      if (!isAlreadyChild) {
        child = await createChildFromDecision(
          asset,
          country,
          launchDate,
          "yes",
        ) as any;
        currentAssets.push(
          enrichAssetsWithVolume([child], state.rules as any)[0] as any,
        );
      }

      const dedupeKey = `${child.id}::${launchDate}`;
      if (!existingForecastKeys.has(dedupeKey)) {
        const forecastPayload = {
          assetId: child.id,
          date: launchDate,
          quantity: Number(
            (child as any).assetVolume || (asset as any)!.assetVolume || 0,
          ),
          owner: country,
          division: asset!.division || "",
          brand: asset!.brand || "",
          country,
          notes: `Bulk validated via ${asset!.productName || asset!.name}`,
        };

        await provider.upsertForecast(forecastPayload);
        currentForecasts.push({
          id: `${child.id}:${launchDate}:${country}`,
          ...forecastPayload,
          createdAt: new Date().toISOString(),
        } as any);
        existingForecastKeys.add(dedupeKey);
      }

      processed += 1;
    }

    updateState({
      assets: currentAssets as any,
      forecasts: currentForecasts as any,
      reviewedMasterIds: buildReviewedMasterIdSet(
        currentAssets as any,
        currentForecasts as any,
      ),
    });
    renderEntryMarketView();
    applyDashboardFiltersAndRender();
    // M1: cross-panel re-renders are automatic via subscribe()

    if (els.marketSelectAll) (els.marketSelectAll as HTMLInputElement).checked = false;
    setMarketMessage(
      `Validated ${processed} row(s). Skipped ${skipped} row(s) without HUB.`,
    );
  } catch (err: any) {
    setMarketMessage(`Bulk validation failed: ${err.message}`, true);
  } finally {
    updateState({ reviewInFlight: false });
    if (els.marketValidateSelectedBtn)
      (els.marketValidateSelectedBtn as HTMLButtonElement).disabled = false;
  }
}

// ── Creator flow (master creation) ───────────────────────────

function bindCreatorFlow(): void {
  if (!els.createMasterBtn || !els.creatorCard) return;

  els.createMasterBtn.addEventListener("click", async () => {
    const provider = getProvider();
    const basePayload = {
      division: (els.creatorDivision as HTMLSelectElement)?.value.trim(),
      brand: (els.creatorBrand as HTMLSelectElement)?.value.trim(),
      hub: (els.creatorHub as HTMLSelectElement)?.value.trim(),
      type: (els.creatorType as HTMLSelectElement)?.value.trim(),
      initiativeOrigin: (els.creatorInitiativeOrigin as HTMLSelectElement)?.value.trim(),
      skyCount: Number((els.creatorSkyCount as HTMLInputElement)?.value || 0),
      projectName: (els.creatorProject as HTMLInputElement)?.value.trim(),
      productName: (els.creatorProduct as HTMLInputElement)?.value.trim(),
      quarter: (els.creatorQuarter as HTMLSelectElement)?.value.trim(),
      requestedDeliveryDate: (els.creatorDate as HTMLInputElement)?.value,
    };

    const eans = String((els.creatorEans as HTMLTextAreaElement)?.value || "")
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

    const variations = String((els.creatorVariations as HTMLInputElement)?.value || "")
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);

    if (
      !basePayload.brand ||
      !basePayload.productName ||
      !basePayload.division ||
      !basePayload.type ||
      !basePayload.initiativeOrigin
    ) {
      setCreatorMessage(
        "Division, Brand, Product Name, Type and Initiative Origin are required.",
        true,
      );
      return;
    }

    if (
      normalizeValue(basePayload.initiativeOrigin) === "local" &&
      !basePayload.projectName
    ) {
      setCreatorMessage(
        "Project Name is required when Origin is Local.",
        true,
      );
      return;
    }

    if (!eans.length) {
      setCreatorMessage("Please paste at least one EAN (one per line).", true);
      return;
    }

    try {
      setCreatorMessage(`Creating ${eans.length} asset line(s)...`);

      const rows = eans.map((ean, i) => {
        const variation = variations[i] || "";
        const productName = variation
          ? `${basePayload.productName} - ${variation}`
          : basePayload.productName;
        return {
          ...basePayload,
          productName,
          globalEAN: ean,
        };
      });

      const result = await processCreatorRows(rows, (msg, isError = false) => {
        setCreatorMessage(msg, isError);
      });

      await triggerRefresh();
      setCreatorMessage(
        `Created ${result.mastersCreated} master line(s) and ${result.childrenCreated} child line(s).`,
      );
      _resetWizard?.();
    } catch (err: any) {
      setCreatorMessage(err.message || "Failed to create master.", true);
    }
  });
}

// N6: live preview of the EAN ↔ Variation positional mapping. Re-binds
// on every init() but handlers are idempotent via a data flag.
function bindEanVariationPreview(): void {
  const ean = els.creatorEans as HTMLTextAreaElement | null;
  // dom.ts typed this as HTMLInputElement, but the HTML is <textarea>.
  // Cast via unknown — the runtime behavior is identical (.value, .addEventListener).
  const variation = els.creatorVariations as unknown as HTMLTextAreaElement | null;
  const host = document.getElementById("ean-variation-preview");
  const body = document.getElementById("ean-variation-preview-body");
  const warning = document.getElementById("ean-variation-preview-warning");
  if (!ean || !variation || !host || !body || !warning) return;

  const refresh = (): void => {
    const { rows, warning: warn } = buildEanVariationPreview(ean.value, variation.value);
    if (!rows.length) {
      host.classList.add("hidden");
      return;
    }
    host.classList.remove("hidden");
    body.innerHTML = rows
      .map(
        (r) => `<tr class="${!r.ean || !r.variation ? "ean-preview-partial" : ""}">
          <td>${r.index}</td>
          <td>${escapeHtml(r.ean || "—")}</td>
          <td>${escapeHtml(r.variation || "—")}</td>
        </tr>`,
      )
      .join("");
    if (warn) {
      warning.textContent = warn;
      warning.classList.remove("hidden");
    } else {
      warning.classList.add("hidden");
    }
  };

  if (!ean.dataset.boundN6) {
    ean.dataset.boundN6 = "1";
    ean.addEventListener("input", refresh);
  }
  if (!variation.dataset.boundN6) {
    variation.dataset.boundN6 = "1";
    variation.addEventListener("input", refresh);
  }
}

function bindCreatorFormInteractions(): void {
  (els.creatorDate as HTMLInputElement)?.addEventListener("change", () => {
    if (els.creatorQuarter) {
      (els.creatorQuarter as HTMLSelectElement).value =
        inferQuarterFromDate((els.creatorDate as HTMLInputElement)?.value || "") || "";
    }
  });

  (els.creatorInitiativeOrigin as HTMLSelectElement)?.addEventListener("change", () => {
    const isLocal =
      normalizeValue((els.creatorInitiativeOrigin as HTMLSelectElement).value) === "local";
    if (els.creatorProject) {
      (els.creatorProject as HTMLInputElement).required = isLocal;
    }
  });

  (els.creatorSkyCount as HTMLInputElement)?.addEventListener("input", () => {
    if (els.creatorSkyCountValue) {
      els.creatorSkyCountValue.textContent = String(
        (els.creatorSkyCount as HTMLInputElement).value || "1",
      );
    }
  });

  (els.creatorDivision as HTMLSelectElement)?.addEventListener(
    "change",
    renderCreatorHubDisplay,
  );
}

function renderCreatorFormOptions(): void {
  const state = getState();
  const selectedDivision = (els.creatorDivision as HTMLSelectElement)?.value || "";
  const selectedBrand = (els.creatorBrand as HTMLSelectElement)?.value || "";
  const selectedType = (els.creatorType as HTMLSelectElement)?.value || "";

  fillSelect(
    els.creatorDivision,
    unique(state.assets.map((a) => a.division)),
    "Select division",
  );
  fillSelect(
    els.creatorBrand,
    unique(state.assets.map((a) => a.brand)),
    "Select brand",
  );
  fillSelect(
    els.creatorType,
    unique(state.assets.map((a) => a.type)),
    "Select type",
  );

  if (els.creatorDivision) (els.creatorDivision as HTMLSelectElement).value = selectedDivision;
  if (els.creatorBrand) (els.creatorBrand as HTMLSelectElement).value = selectedBrand;
  if (els.creatorType) (els.creatorType as HTMLSelectElement).value = selectedType;
  if (els.creatorSkyCountValue && els.creatorSkyCount) {
    els.creatorSkyCountValue.textContent = String(
      (els.creatorSkyCount as HTMLInputElement).value || "1",
    );
  }

  renderCreatorHubDisplay();
}

function renderCreatorHubDisplay(): void {
  if (!els.creatorHub) return;

  const state = getState();
  const divisionNeedle = normalizeValue(
    (els.creatorDivision as HTMLSelectElement)?.value || "",
  );
  const hubsFromRules = unique(
    (state.rules || [])
      .filter(
        (rule) =>
          !divisionNeedle ||
          normalizeValue(rule.division) === divisionNeedle,
      )
      .map((rule) => String(rule.hub || "").trim())
      .filter(Boolean),
  );

  if (!hubsFromRules.length) {
    els.creatorHub.innerHTML =
      '<option value="">Auto from Forecast_Rules</option>';
    return;
  }

  els.creatorHub.innerHTML = hubsFromRules
    .map(
      (hub) =>
        `<option value="${escapeHtml(hub)}">${escapeHtml(hub)}</option>`,
    )
    .join("");
}

function renderLocalProjectOptions(): void {
  const state = getState();
  const selectedDivision = (els.localDivision as HTMLSelectElement)?.value || "";
  const selectedBrand = (els.localBrand as HTMLSelectElement)?.value || "";
  const selectedHub = (els.localHub as HTMLSelectElement)?.value || "";
  const selectedType = (els.localType as HTMLSelectElement)?.value || "";

  fillSelect(
    els.localDivision,
    unique(state.assets.map((a) => a.division)),
    "Select division",
  );
  fillSelect(
    els.localBrand,
    unique(state.assets.map((a) => a.brand)),
    "Select brand",
  );
  fillSelect(
    els.localHub,
    unique(state.assets.map((a) => a.hub)),
    "Select hub",
  );
  fillSelect(
    els.localType,
    unique(state.assets.map((a) => a.type)),
    "Select type",
  );

  if (els.localDivision) (els.localDivision as HTMLSelectElement).value = selectedDivision;
  if (els.localBrand) (els.localBrand as HTMLSelectElement).value = selectedBrand;
  if (els.localHub) (els.localHub as HTMLSelectElement).value = selectedHub;
  if (els.localType) (els.localType as HTMLSelectElement).value = selectedType;
}

// ── Local project flow ───────────────────────────────────────

function bindLocalProjectFlow(): void {
  (els.localDate as HTMLInputElement)?.addEventListener("change", () => {
    if (els.localQuarter) {
      (els.localQuarter as HTMLSelectElement).value =
        inferQuarterFromDate((els.localDate as HTMLInputElement).value) || "";
    }
  });

  els.createLocalProjectBtn?.addEventListener("click", async () => {
    const division = (els.localDivision as HTMLSelectElement)?.value.trim();
    const brand = (els.localBrand as HTMLSelectElement)?.value.trim();
    const hub = (els.localHub as HTMLSelectElement)?.value.trim();
    const type = (els.localType as HTMLSelectElement)?.value.trim();
    const requestedDeliveryDate = (els.localDate as HTMLInputElement)?.value;
    const quarter = (els.localQuarter as HTMLSelectElement)?.value.trim();
    const projectName = (els.localProject as HTMLInputElement)?.value.trim();
    const productBaseName =
      (els.localProduct as HTMLInputElement)?.value.trim() || projectName;
    const assetCount = Number((els.localAssetCount as HTMLInputElement)?.value || 0);

    if (
      !division ||
      !brand ||
      !hub ||
      !type ||
      !requestedDeliveryDate ||
      !quarter ||
      !projectName ||
      !productBaseName
    ) {
      setLocalMessage(
        "Division, Brand, Hub, Type, Date, Quarter and Project Name are required.",
        true,
      );
      return;
    }
    if (!Number.isFinite(assetCount) || assetCount < 1) {
      setLocalMessage("Number of Assets must be at least 1.", true);
      return;
    }

    try {
      const rows = Array.from({ length: assetCount }, (_, index) => {
        const seq = index + 1;
        return {
          division,
          brand,
          hub,
          type,
          initiativeOrigin: "Local",
          skyCount: assetCount,
          projectName,
          productName: `${productBaseName} - Asset ${seq}`,
          quarter,
          requestedDeliveryDate,
          globalEAN: `LOCAL-${Date.now()}-${seq}-${hub}`,
        };
      });

      const result = await processCreatorRows(
        rows,
        (msg, isError = false) => setLocalMessage(msg, isError),
      );
      await triggerRefresh();
      setLocalMessage(
        `Created ${result.mastersCreated} master line(s) and ${result.childrenCreated} child line(s).`,
      );
    } catch (err: any) {
      setLocalMessage(
        err.message || "Failed to create local project.",
        true,
      );
    }
  });
}

// ── View toggle (tinder vs market) ───────────────────────────

function bindViewToggle(): void {
  if (!els.viewToggleBtns || !els.viewToggleBtns.length) return;

  for (const btn of els.viewToggleBtns) {
    btn.addEventListener("click", (e: Event) => {
      const view = (e.currentTarget as HTMLElement).dataset.view;
      const showMaster = view === "tinder";

      els.entryMaster?.classList.toggle("is-active", showMaster);
      els.entryMaster?.classList.toggle("hidden", !showMaster);
      els.entryLocal?.classList.toggle("is-active", !showMaster);
      els.entryLocal?.classList.toggle("hidden", showMaster);

      if (!showMaster) renderEntryMarketView();

      for (const b of els.viewToggleBtns) {
        b.classList.toggle("is-active", (b as HTMLElement).dataset.view === view);
      }
    });
  }
}

// ── Creator tabs (form / csv / bulk) ─────────────────────────

function bindCreatorTabs(): void {
  if (!els.tabBtns || !els.tabBtns.length) return;

  for (const btn of els.tabBtns) {
    btn.addEventListener("click", (e: Event) => {
      const tab = (e.target as HTMLElement).dataset.tab;

      for (const b of els.tabBtns) {
        b.classList.toggle("is-active", (b as HTMLElement).dataset.tab === tab);
      }

      els.creatorFormTab?.classList.toggle("hidden", tab !== "form");
      els.creatorFormTab?.classList.toggle("is-active", tab === "form");
      els.creatorCsvTab?.classList.toggle("hidden", tab !== "csv");
      els.creatorCsvTab?.classList.toggle("is-active", tab === "csv");
      els.creatorBulkTab?.classList.toggle("hidden", tab !== "bulk");
      els.creatorBulkTab?.classList.toggle("is-active", tab === "bulk");
    });
  }
}

// ── Wizard navigation (EF-9 / ticket 819a) ──────────────────

function bindWizardNav(): void {
  function goToStep(step: number): void {
    document.querySelectorAll<HTMLElement>("[data-wizard-step]").forEach((btn) => {
      const n = Number(btn.dataset.wizardStep);
      btn.classList.toggle("is-active", n === step);
      btn.classList.toggle("is-done", n < step);
    });
    document.querySelectorAll<HTMLElement>("[data-wizard-panel]").forEach((panel) => {
      const n = Number(panel.dataset.wizardPanel);
      panel.classList.toggle("is-active", n === step);
      panel.classList.toggle("hidden", n !== step);
    });
  }

  _resetWizard = () => goToStep(1);

  // Next: Step 1 → 2 (validate Project Name)
  document.getElementById("wizard-next-1")?.addEventListener("click", () => {
    const project = (els.creatorProject as HTMLInputElement)?.value.trim();
    const msgEl = document.getElementById("creator-message-step1");
    if (!project) {
      if (msgEl) { msgEl.textContent = "Project Name is required."; msgEl.className = "message error"; }
      return;
    }
    if (msgEl) { msgEl.textContent = ""; msgEl.className = "message"; }
    goToStep(2);
  });

  // Next: Step 2 → 3
  document.getElementById("wizard-next-2")?.addEventListener("click", () => goToStep(3));

  // Back buttons
  document.getElementById("wizard-back-2")?.addEventListener("click", () => goToStep(1));
  document.getElementById("wizard-back-3")?.addEventListener("click", () => goToStep(2));

  // Clicking a completed step indicator jumps back
  document.querySelectorAll<HTMLElement>("[data-wizard-step]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const stepNum = Number(btn.dataset.wizardStep);
      if (btn.classList.contains("is-done")) goToStep(stepNum);
    });
  });
}

// ── CSV upload ───────────────────────────────────────────────

// T10: in-memory state for CSV preview/validation between change → upload
let lastValidations: MasterCsvRowValidation[] = [];
// N7: active project type (master vs local) — selected via the Project Type dropdown.
type CsvProjectType = "master" | "local";
function getCsvProjectType(): CsvProjectType {
  const sel = document.getElementById("csv-project-type") as HTMLSelectElement | null;
  return (sel?.value as CsvProjectType) || "master";
}

function bindCsvUpload(): void {
  if (!els.csvFileInput || !els.csvUploadBtn) return;

  // T10/N7: Download CSV template button — switches between Master and
  // Local templates based on the Project Type dropdown.
  document.getElementById("csv-template-btn")?.addEventListener("click", () => {
    const type = getCsvProjectType();
    if (type === "local") {
      triggerDownload(buildLocalCsvTemplate(), "local-projects-template.csv");
    } else {
      triggerDownload(buildMasterCsvTemplate(), "master-projects-template.csv");
    }
  });

  // N7: when the Project Type selector changes, update the subtitle and
  // columns hint, restrict Local mode to Market-role users, and clear the
  // preview so the old validation doesn't confuse the user.
  const projectTypeSel = document.getElementById("csv-project-type") as HTMLSelectElement | null;
  const columnsLabel = document.getElementById("csv-columns-label");
  const subtitle = document.getElementById("csv-subtitle");
  const refreshCsvTypeUI = (): void => {
    const type = getCsvProjectType();
    if (columnsLabel) {
      const cols = type === "local" ? LOCAL_CSV_TEMPLATE_COLUMNS : MASTER_CSV_TEMPLATE_COLUMNS;
      columnsLabel.firstChild!.textContent = `CSV File (columns: ${cols.join(", ")})`;
    }
    if (subtitle) {
      subtitle.textContent =
        type === "local"
          ? "Local Projects are created per market. Only Market-role users can import them. Download the template, fill it, preview, then import."
          : "Download the template, fill it row-by-row, then upload and preview before final import. Invalid rows are highlighted in the preview.";
    }
    // Enforce role restriction: hide Local option entirely for non-Market roles
    // (ECF is read-only, Admin handles Master). Re-run at every role switch by
    // listening to role changes would require wiring; for now we disable on mount.
    lastValidations = [];
    const preview = document.getElementById("csv-preview");
    if (preview) {
      preview.classList.add("hidden");
      preview.innerHTML = "";
    }
    document.getElementById("csv-error-report-btn")?.classList.add("hidden");
  };
  projectTypeSel?.addEventListener("change", refreshCsvTypeUI);
  // N7: gate Local option by current role. If the user is NOT Market/Admin,
  // hide the Local entry from the dropdown so they can't select it.
  const role = getState().role;
  if (projectTypeSel && role !== "market" && role !== "admin") {
    const localOpt = projectTypeSel.querySelector('option[value="local"]');
    localOpt?.remove();
  }

  // T10: Download error report button (rebuilt from lastValidations)
  document.getElementById("csv-error-report-btn")?.addEventListener("click", () => {
    const report = buildErrorReportCsv(lastValidations);
    triggerDownload(report, "csv-error-report.csv");
  });

  els.csvFileInput.addEventListener("change", async (e: Event) => {
    const input = e.target as HTMLInputElement;
    if (!input.files?.length) return;
    (els.csvUploadBtn as HTMLButtonElement).disabled = false;
    // T10/N7: parse + validate using the active template (Master or Local)
    try {
      const text = await input.files[0].text();
      const parsed = parseCsv(text);
      const type = getCsvProjectType();
      lastValidations =
        type === "local"
          ? validateLocalCsvRows(parsed.headers, parsed.rows)
          : validateMasterCsvRows(parsed.headers, parsed.rows);
      renderCsvPreview(parsed.headers, lastValidations);
    } catch (err: any) {
      if (els.csvMessage) {
        els.csvMessage.textContent = err?.message || "Failed to read CSV.";
        els.csvMessage.classList.add("error");
      }
    }
  });

  els.csvUploadBtn.addEventListener("click", async () => {
    const file = (els.csvFileInput as HTMLInputElement).files![0];
    if (!file) return;

    const loadingContainer = els.creatorCard as HTMLElement | null;
    if (loadingContainer) showLoading(loadingContainer);
    try {
      const text = await file.text();
      const rows = parseCsvToRows(text);
      if (!rows.length) {
        if (els.csvMessage) {
          els.csvMessage.textContent = "CSV must have at least one data row.";
          els.csvMessage.classList.add("error");
        }
        return;
      }

      if (els.csvMessage) {
        els.csvMessage.textContent = `Uploading ${rows.length} row(s)...`;
        els.csvMessage.classList.remove("error");
      }
      const result = await processCreatorRows(rows, (msg, isError = false) => {
        if (els.csvMessage) {
          els.csvMessage.textContent = msg;
          els.csvMessage.classList.toggle("error", isError);
        }
      });

      if (els.csvMessage) {
        // T10: explicit "X created, Y skipped" summary
        const skipped = lastValidations.filter((v) => v.errors.length).length;
        els.csvMessage.textContent = `${result.mastersCreated} project(s) created, ${skipped} row(s) skipped.`;
        els.csvMessage.classList.remove("error");
      }
      // T10: surface success as a toast as well
      showToast(
        `${result.mastersCreated} project(s) created, ${result.childrenCreated} variation(s).`,
        "success",
      );
      await triggerRefresh();
    } catch (err: any) {
      if (els.csvMessage) {
        els.csvMessage.textContent = err.message || "CSV upload failed.";
        els.csvMessage.classList.add("error");
      }
    } finally {
      if (loadingContainer) hideLoading(loadingContainer);
    }
  });
}

// T10: render preview with row-by-row validation. Invalid rows get a
// `csv-row-invalid` class so the user can spot them. Toggles the "Download
// error report" button visibility based on whether any errors exist.
function renderCsvPreview(
  _headers: string[],
  validations: MasterCsvRowValidation[],
): void {
  const host = document.getElementById("csv-preview");
  const errorBtn = document.getElementById("csv-error-report-btn");
  if (!host) return;

  if (!validations.length) {
    host.classList.add("hidden");
    errorBtn?.classList.add("hidden");
    return;
  }

  const invalidCount = validations.filter((v) => v.errors.length).length;
  errorBtn?.classList.toggle("hidden", invalidCount === 0);

  const headerHtml = `<tr>${["#", ...MASTER_CSV_TEMPLATE_COLUMNS, "Errors"]
    .map((h) => `<th>${escapeHtml(h)}</th>`)
    .join("")}</tr>`;

  const bodyHtml = validations
    .slice(0, 50)
    .map((v) => {
      const cells = MASTER_CSV_TEMPLATE_COLUMNS.map(
        (col) => `<td>${escapeHtml(v.values[col] || "")}</td>`,
      ).join("");
      const errs = v.errors.join("; ");
      return `<tr class="${v.errors.length ? "csv-row-invalid" : ""}">
        <td>${v.rowIndex}</td>
        ${cells}
        <td>${escapeHtml(errs)}</td>
      </tr>`;
    })
    .join("");

  host.innerHTML = `
    <p class="card-subtitle">Preview: ${validations.length} row(s), ${invalidCount} invalid. ${validations.length > 50 ? "Showing first 50." : ""}</p>
    <table class="csv-preview-table"><thead>${headerHtml}</thead><tbody>${bodyHtml}</tbody></table>
  `;
  host.classList.remove("hidden");
}

function triggerDownload(content: string, filename: string): void {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ── Bulk entry ───────────────────────────────────────────────

function bindBulkEntry(): void {
  if (!els.bulkCreateBtn) return;

  els.bulkCreateBtn.addEventListener("click", async () => {
    try {
      const basePayload = {
        division: (els.creatorDivision as HTMLSelectElement)?.value.trim(),
        brand: (els.creatorBrand as HTMLSelectElement)?.value.trim(),
        hub: (els.creatorHub as HTMLSelectElement)?.value.trim(),
        type: (els.creatorType as HTMLSelectElement)?.value.trim(),
        initiativeOrigin: (els.creatorInitiativeOrigin as HTMLSelectElement)?.value.trim(),
        skyCount: Number((els.creatorSkyCount as HTMLInputElement)?.value || 0),
        projectName:
          (els.bulkProject as HTMLInputElement)?.value.trim() ||
          (els.creatorProject as HTMLInputElement)?.value.trim() ||
          "",
        productName: (els.creatorProduct as HTMLInputElement)?.value.trim(),
        quarter: (els.creatorQuarter as HTMLSelectElement)?.value.trim(),
        requestedDeliveryDate: (els.creatorDate as HTMLInputElement)?.value || "",
      };

      const pairs = String((els.bulkSkus as HTMLTextAreaElement)?.value || "")
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          const [sku, variation] = line.split("|").map((v) => (v || "").trim());
          return { sku, variation };
        })
        .filter((row) => row.sku);

      const rows = pairs.map(({ sku, variation }) => ({
        ...basePayload,
        productName: variation
          ? `${basePayload.productName || basePayload.projectName || "Product"} - ${variation}`
          : basePayload.productName || basePayload.projectName || sku,
        globalEAN: sku,
      }));

      if (!rows.length) {
        if (els.bulkMessage) {
          els.bulkMessage.textContent =
            "Please add at least one SKU line (SKU|Variation).";
          els.bulkMessage.classList.add("error");
        }
        return;
      }

      const result = await processCreatorRows(rows, (msg, isError = false) => {
        if (els.bulkMessage) {
          els.bulkMessage.textContent = msg;
          els.bulkMessage.classList.toggle("error", isError);
        }
      });

      if (els.bulkMessage) {
        els.bulkMessage.textContent = `Created ${result.mastersCreated} master row(s) and ${result.childrenCreated} child row(s).`;
        els.bulkMessage.classList.remove("error");
      }
      await triggerRefresh();
    } catch (err: any) {
      if (els.bulkMessage) {
        els.bulkMessage.textContent = err.message || "Bulk create failed.";
        els.bulkMessage.classList.add("error");
      }
    }
  });
}
